<div class="mx-auto max-w-(--breakpoint-2xl) px-3">
  <div class="w-full">
    <div class="my-10 text-center">
      <div class="flex flex-wrap items-center justify-center gap-2">
        <h2 class="text-2xl font-medium text-gray-800 dark:text-white/90">
          @if (product.id) {
            Edit Product
          } @else {
            Add Product
          }
        </h2>
        @if (product.id) {
          <span
            class="inline-flex items-center text-gray-500 dark:text-gray-400 cursor-help"
            [appTippy]="'Product ID: ' + product.id"
          >
            <lucide-icon [name]="InfoIcon" size="14"></lucide-icon>
          </span>
        }
      </div>
    </div>
  </div>

  <form [formGroup]="form">

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">

      <!-- Product Information -->
      <div class="lg:col-span-2 rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">

        <div class="px-5 py-4 sm:px-6 sm:py-5">
          <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
            Product Information
          </h3>
        </div>

        <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <app-text-input [control]="form.controls.name" [name]="'name'" [placeholder]="'Product Name'" [label]="'Name'"></app-text-input>
            </div>

            <div>
              <app-text-input [control]="form.controls.price" [mask]="'separator.2'" [thousandSeparator]="','" [allowNegativeNumbers]="true" [name]="'price'" [placeholder]="'0.00'" [label]="'&pound; Price'"></app-text-input>
            </div>

            <div class="sm:col-span-2">
              @if (editor) {
                <div class="NgxEditor__Wrapper min-h-40 h-auto">
                  <ngx-editor-menu [editor]="editor" [toolbar]="toolbar"></ngx-editor-menu>
                  <ngx-editor
                    [formControl]="form.controls.description"
                    [editor]="editor"
                    [placeholder]="'Type here...'"
                  ></ngx-editor>
                </div>
              }
            </div>

            <div>
              <app-select [control]="form.controls.status" [choices]="statusChoices" label="Status"></app-select>
            </div>

            <div>
              <app-select [control]="form.controls.category" [choices]="categoryChoices" label="Category"></app-select>
            </div>

            <div>
              <app-date-picker
                [control]="form.controls.available_from"
                [name]="'available_from'"
                [label]="'Available From (optional)'"
              ></app-date-picker>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Used to filter event product search by the event date.
              </p>
            </div>

            <div>
              <app-date-picker
                [control]="form.controls.available_until"
                [name]="'available_until'"
                [label]="'Available Until (optional)'"
                [minDate]="form.controls.available_from.value || undefined"
              ></app-date-picker>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Used to phase out products for new events after this date.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Configuration Section -->
      <div class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="px-5 py-4 sm:px-6 sm:py-5">
          <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
            Configuration
          </h3>
        </div>

        <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">

          <!-- Minimum Spend -->
          <div>
            <app-checkbox [control]="form.controls.is_minimum_spend" label="Include in minimum spend"></app-checkbox>
          </div>

          <!-- Variant-Based Pricing -->
          <div>
            <app-checkbox [control]="form.controls.is_variant_based" label="Variant-Based Pricing"></app-checkbox>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Product price calculated solely from variant option prices
            </p>
            @if (form.controls.is_variant_based.value) {
              <div class="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p class="text-sm text-blue-800 dark:text-blue-200">
                  <lucide-icon [name]="InfoIcon" class="inline-block w-4 h-4 mr-1 flex-shrink-0"></lucide-icon>
                  <strong>Note:</strong> Requires variants with priced options
                </p>
              </div>
            }
          </div>

          <!-- £500 Offer Configuration -->
          <div class="pt-4 border-t border-gray-100 dark:border-gray-800">
            <h4 class="text-sm font-semibold text-gray-800 dark:text-white/90 mb-3">£500 Offer</h4>
            <div class="space-y-4">
              <div>
                <app-checkbox [control]="form.controls.counts_to_500_deal" label="Counts to £500 Deal"></app-checkbox>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Counts toward £500 qualifying threshold
                </p>
              </div>
              <div>
                <app-checkbox [control]="form.controls.inc_in_150_free" label="Available in £150 Offer"></app-checkbox>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Can be selected for £150 discount
                </p>
              </div>
            </div>
          </div>

          <!-- TVG-57: Accommodation Tab Trigger -->
          <div class="pt-4 border-t border-gray-100 dark:border-gray-800">
            <h4 class="text-sm font-semibold text-gray-800 dark:text-white/90 mb-3">Accommodation</h4>
            <div>
              <app-checkbox [control]="form.controls.triggers_accommodation_tab" label="Triggers Accommodation Tab"></app-checkbox>
              <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Real accommodation products (rooms) — turn this on so the event's
                Accommodation tab appears. Leave off for accommodation-category
                supplements (e.g. no-accommodation charges).
              </p>
            </div>
          </div>

        </div>
      </div>

      @if (product.id) {
        <div class="lg:col-span-3">
          <div class="rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div class="px-5 py-4 sm:px-6 sm:py-5">
              <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
                Product Variants
              </h3>
            </div>
            <div class="p-5 border-t border-gray-100 dark:border-gray-800 sm:p-6">
              <div class="flex flex-col gap-4">
                <div class="flex flex-wrap items-end gap-3">
                  <div class="flex-1 min-w-[200px]">
                    <app-text-input
                      [control]="variantNameControl"
                      [name]="'variantName'"
                      [placeholder]="'Variant name'"
                      [label]="'Variant name'">
                    </app-text-input>
                  </div>
                  <div class="flex-[2] min-w-[240px]">
                    <app-text-input
                      [control]="variantDescriptionControl"
                      [name]="'variantDescription'"
                      [placeholder]="'Short description'"
                      [label]="'Description (optional)'">
                    </app-text-input>
                  </div>
                  <button (click)="addVariant()"
                          [disabled]="!variantNameControl.value || variantNameControl.value.trim() === ''"
                          [class]="'h-12 text-theme-sm shadow-theme-xs inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 font-medium ' +
                                   (!variantNameControl.value || variantNameControl.value.trim() === '' ?
                                    'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-500' :
                                    'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200')">
                    <lucide-icon [name]="PlusIcon"></lucide-icon>
                    Add Variant
                  </button>
                </div>
                @if (variantCreateError) {
                  <p class="text-sm text-red-600 dark:text-red-400">{{ variantCreateError }}</p>
                }

                @if (variantsPage?.data?.length) {
                  <div class="space-y-4">
                    @for (variant of sortedVariants; track variant.id) {
                      <div class="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900/40">
                        <div class="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
                          <div class="min-w-[200px]">
                            <div class="flex items-baseline gap-2">
                              <p class="text-base font-semibold text-gray-900 dark:text-white/90">{{ variant.name }}</p>
                              <span class="text-xs text-gray-500 dark:text-gray-400">#{{ variant.id }}</span>
                            </div>
                            <p class="max-w-[320px] truncate text-xs text-gray-500 dark:text-gray-400">
                              {{ variant.description || '' }}
                            </p>
                          </div>
                          <div class="flex items-center gap-2">
                            <button
                              type="button"
                              (click)="openVariantUsage(variant)"
                              class="text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-500"
                              title="View usage"
                            >
                              <lucide-icon [size]="18" [name]="InfoIcon"></lucide-icon>
                            </button>
                            <button
                              type="button"
                              (click)="startVariantEdit(variant)"
                              class="text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-500"
                              title="Edit variant"
                            >
                              <lucide-icon [size]="18" [name]="EditIcon"></lucide-icon>
                            </button>
                            <button
                              type="button"
                              (click)="deleteVariant(variant)"
                              class="text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500"
                              title="Delete variant"
                            >
                              <lucide-icon [size]="18" [name]="TrashIcon"></lucide-icon>
                            </button>
                          </div>
                        </div>

                        @if (editingVariantId === variant.id) {
                          <div class="border-b border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-800/40">
                            <div class="flex flex-wrap gap-3">
                              <div class="flex-1 min-w-[200px]">
                                <app-text-input
                                  [control]="variantEditForm.controls.name"
                                  [name]="'variantEditName'"
                                  [placeholder]="'Variant name'"
                                  [label]="'Variant name'"
                                ></app-text-input>
                              </div>
                              <div class="flex-[2] min-w-[240px]">
                                <app-text-input
                                  [control]="variantEditForm.controls.description"
                                  [name]="'variantEditDescription'"
                                  [placeholder]="'Short description'"
                                  [label]="'Description (optional)'"
                                ></app-text-input>
                              </div>
                              <div class="min-w-[240px]">
                                <app-date-picker
                                  [control]="variantEditForm.controls.available_from"
                                  [name]="'variantAvailableFrom'"
                                  [label]="'Variant Available From'"
                                ></app-date-picker>
                              </div>
                              <div class="min-w-[240px]">
                                <app-date-picker
                                  [control]="variantEditForm.controls.available_until"
                                  [name]="'variantAvailableUntil'"
                                  [label]="'Variant Available Until'"
                                  [minDate]="variantEditForm.controls.available_from.value || undefined"
                                ></app-date-picker>
                              </div>
                            </div>
                            @if (variantEditError) {
                              <p class="mt-2 text-sm text-red-600 dark:text-red-400">{{ variantEditError }}</p>
                            }
                            <div class="mt-3 flex flex-wrap gap-2">
                              <button
                                type="button"
                                (click)="saveVariantEdit()"
                                class="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                (click)="cancelVariantEdit()"
                                class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        }

                        <div class="px-4 py-4">
                          <app-product-variant-options
                            [item]="variant"
                            [activeVariantId]="activeOptionVariantId"
                            (formOpen)="openOptionForm($event)"
                            (formClose)="closeOptionForm()"
                          ></app-product-variant-options>
                        </div>
                      </div>
                    }
                  </div>
                } @else {
                  <div class="rounded-lg border border-dashed border-gray-200 p-6 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    No variants yet. Add one to start configuring options.
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      } @else {
        <div class="lg:col-span-3">
          <div class="rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div class="px-5 py-4 sm:px-6 sm:py-5">
              <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
                Product Variants
              </h3>
            </div>
            <div class="p-5 border-t border-gray-100 dark:border-gray-800 sm:p-6">
              <div class="text-center py-8">
                <div class="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
                  <lucide-icon [name]="InfoIcon" class="w-6 h-6 text-blue-600 dark:text-blue-400"></lucide-icon>
                </div>
                <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-2">Save Product First</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                  You need to save this product before you can add variants. Click the "Save" button below to continue.
                </p>
              </div>
            </div>
          </div>
        </div>

      }

      <!-- Product Image -->
      <div class="lg:col-span-3 rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="px-5 py-4 sm:px-6 sm:py-5">
          <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
            Product Image
          </h3>
        </div>

        <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">
          <div class="space-y-5">

            <div class="h-full w-full flex flex-col items-start justify-between">
              @if (imageUrl) {
                <img [src]="imageUrl" alt="Selected Image" class="mt-2 max-w-[368px] max-h-[232px]">
              } @else {
                @if (product.images.length > 0 && product.images[0].sm) {
                  <img [src]="product.images[0].sm" alt="Product Image" class="h-full mt-2">
                }
              }
              <div class="w-full mt-5">
                <input type="file" (change)="onFileChange($event)"
                       class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full
                       file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100">
              </div>
            </div>

          </div>

        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      @if (variantToDelete && !variantUsageDetails) {
        <app-modal
          [title]="'Delete Variant'"
          [content]="'Are you sure you want to delete variant - ' + variantToDelete.name + '?'"
          [show]="!!variantToDelete"
          [closeButtonLabel]="'Cancel'"
          [confirmButtonLabel]="'Yes, Proceed'"
          (onClose)="closeVariantDeleteModal()"
          (onConfirm)="confirmDelete()"
        ></app-modal>
      }

      <!-- Variant Usage Modal -->
      @if (variantUsageDetails) {
        <app-modal
          [title]="variantUsageContext === 'delete' ? 'Cannot Delete Variant' : 'Variant Usage'"
          [show]="!!variantUsageDetails"
          [closeButtonLabel]="'Close'"
          [confirm]="false"
          (onClose)="closeVariantUsageModal()"
        >
          <div class="space-y-4">
            @if (variantUsageDetails.event_count > 0) {
              <p class="text-sm text-gray-700 dark:text-gray-300">
                @if (variantUsageContext === 'delete') {
                  This variant cannot be deleted because it is currently being used in <strong>{{ variantUsageDetails.event_count }}</strong> event(s):
                } @else {
                  {{ variantUsageVariant?.name || 'This variant' }} is currently used in <strong>{{ variantUsageDetails.event_count }}</strong> event(s):
                }
              </p>

              <div class="max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table class="min-w-full text-sm">
                  <thead class="bg-gray-50 dark:bg-gray-800/40">
                    <tr>
                      <th class="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Event</th>
                      <th class="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Date</th>
                      <th class="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Open</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                    @for (event of variantUsageDetails.events; track event.id) {
                      <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td class="px-4 py-3">
                          <a [href]="'/events/' + event.id"
                             target="_blank"
                             class="font-medium text-gray-900 hover:text-brand-600 dark:text-white dark:hover:text-brand-400">
                            {{ event.name }}
                          </a>
                          <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            @if (event.reference) {
                              Ref: {{ event.reference }}
                            } @else {
                              ID: {{ event.id }}
                            }
                          </div>
                        </td>
                        <td class="px-4 py-3 text-gray-500 dark:text-gray-400">
                          {{ event.date || '—' }}
                        </td>
                        <td class="px-4 py-3 text-right">
                          <a [href]="'/events/' + event.id"
                             target="_blank"
                             class="inline-flex items-center gap-1 text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400">
                            View
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                            </svg>
                          </a>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              @if (variantUsageContext === 'delete') {
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  To delete this variant, please remove it from these events first.
                </p>
              }
            } @else {
              <p class="text-sm text-gray-600 dark:text-gray-400">
                {{ variantUsageVariant?.name || 'This variant' }} is not used in any events yet.
              </p>
            }
          </div>
        </app-modal>
      }

      @if (product.id) {
        <app-modal
          [title]="'Duplicate Product'"
          [show]="duplicateModalOpen"
          [closeButtonLabel]="'Cancel'"
          [confirmButtonLabel]="'Create Copy'"
          [processing]="duplicateProcessing"
          (onClose)="cancelDuplicate()"
          (onConfirm)="duplicateProduct()"
        >
          <div class="space-y-3 text-left">
            <p class="text-sm text-gray-600 dark:text-gray-400">You are about to duplicate:</p>
            <p class="text-base font-semibold text-gray-900 dark:text-white break-words">
              “{{ product.name || 'Unnamed product' }}”
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              The new product will be saved as:
              <span class="font-medium text-gray-800 dark:text-gray-200 break-words">“{{ duplicateNamePreview() }}”</span>
            </p>
            @if (duplicateProcessing) {
              <div class="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-900/60 dark:bg-blue-900/20 dark:text-blue-200">
                <div class="font-medium">Status: {{ duplicateStatusLabel() }}</div>
                <div>This can take a minute for large products. Please keep this window open.</div>
              </div>
            }
          </div>
        </app-modal>
      }

    </div>


    <div class="mt-8 flex items-center justify-end gap-3">
      <app-button
        [label]="'Cancel'"
        [style]="'secondary'"
        (click)="cancel()"
      ></app-button>

      @if (product.id) {
        <app-button
          [label]="'Duplicate'"
          [style]="'secondary'"
          [disabled]="duplicateProcessing"
          (click)="openDuplicateModal()"
        ></app-button>
      }

      <app-button
        [label]="'Save & Continue'"
        [style]="'secondary'"
        [disabled]="!canSave || processing"
        (click)="saveAndContinue()"
      ></app-button>

      <app-button
        [label]="'Save'"
        [style]="'primary'"
        [disabled]="!canSave || processing"
        (click)="save()"
      ></app-button>
    </div>

    <!--  @if (validationErrors.length > 0) {-->
    <!--    <div class="mt-4 p-4 border border-red-500 bg-red-100 text-red-700 rounded-lg">-->
    <!--      <h3 class="font-medium">Validation Errors:</h3>-->
    <!--      <ul class="list-disc list-inside">-->
    <!--        @for (error of validationErrors; track error) {-->
    <!--          <li>{{ error }}</li>-->
    <!--        }-->
    <!--      </ul>-->
    <!--    </div>-->
    <!--  }-->

  </form>
</div>
